<template>
  <div class="ntfy-setup-container">
    <div class="setup-header">
      <h1 class="has-text-weight-semibold is-size-4">Setup Notifications</h1>
      <p class="subtitle">Receive real-time notifications about your call requests</p>
    </div>

    <div class="setup-steps">

      <!-- Step 2: iOS Setup -->
      <div class="step-card">
        <div class="step-header">
          <span class="step-number">1</span>
          <h2>iOS Setup Instructions</h2>
        </div>
        <div class="step-content">
          <div class="os-icon">
            <i class="fab fa-apple"></i>
          </div>
          <ol class="instructions-list">
            <li>Install the ntfy app from the App Store</li>
            <li>Open the ntfy app and tap the "+" button</li>
            <li>Select "Subscribe to topic"</li>
            <li>Enter your topic:
              <div class="copy-field-group">
                <code class="inline-code">{{ fullTopic }}</code>
                <button @click="copyToClipboard(fullTopic, 'topic')" class="mini-copy-btn">
                  <i class="fas fa-copy"></i>
                </button>
              </div>
            </li>
            <li>Choose "Use another server", then add:
              <div class="copy-field-group">
                <code class="inline-code">{{ serverUrl }}</code>
                <button @click="copyToClipboard(serverUrl, 'server')" class="mini-copy-btn">
                  <i class="fas fa-copy"></i>
                </button>
              </div>
            </li>
            <li>Tap "Save"</li>
            <li>Enable notifications in iOS Settings if prompted</li>
          </ol>
          <div class="app-store-badges">
            <a href="https://apps.apple.com/app/ntfy/id1625396347" target="_blank" class="app-store-link">
              <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" class="app-store-badge">
            </a>
          </div>
        </div>
      </div>

      <!-- Step 3: Android Setup -->
      <div class="step-card">
        <div class="step-header">
          <span class="step-number">2</span>
          <h2>Android Setup Instructions</h2>
        </div>
        <div class="step-content">
          <div class="os-icon">
            <i class="fab fa-android"></i>
          </div>
          <ol class="instructions-list">
            <li>Install the ntfy app from Google Play Store</li>
            <li>Open the ntfy app and tap the "+" button</li>
            <li>Select "Subscribe to topic"</li>
            <li>Enter your topic:
              <div class="copy-field-group">
                <code class="inline-code">{{ fullTopic }}</code>
                <button @click="copyToClipboard(fullTopic, 'topic')" class="mini-copy-btn">
                  <i class="fas fa-copy"></i>
                </button>
              </div>
            </li>
            <li>In the server field, enter:
              <div class="copy-field-group">
                <code class="inline-code">{{ serverUrl }}</code>
                <button @click="copyToClipboard(serverUrl, 'server')" class="mini-copy-btn">
                  <i class="fas fa-copy"></i>
                </button>
              </div>
            </li>
            <li>Tap "Save"</li>
            <li>Allow notification permissions if prompted</li><!-- 
            <li>For better battery life, go to App Settings → Battery → Unrestricted</li> -->
          </ol>
          <div class="app-store-badges">
            <a href="https://play.google.com/store/apps/details?id=io.heckel.ntfy" target="_blank" class="play-store-link">
              <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" class="play-store-badge">
            </a>
          </div>
        </div>
      </div>


      <!-- Step 5: Testing & Troubleshooting -->
      <div class="step-card">
        <div class="step-header">
          <span class="step-number">3</span>
          <h2>Troubleshooting</h2>
        </div>
        <div class="step-content">

          <h3>Common Issues</h3>
          <div class="troubleshooting-tips">
            <div class="tip">
              <strong>Notifications not arriving?</strong>
              <div>• Make sure you're using the correct server: 
                <div class="copy-field-group">
                  <code class="inline-code">{{ serverUrl }}</code>
                  <button @click="copyToClipboard(serverUrl, 'server')" class="mini-copy-btn">
                    <i class="fas fa-copy"></i>
                  </button>
                </div>
              </div>
              <div>• Verify the topic is exactly: 
                <div class="copy-field-group">
                  <code class="inline-code">{{ fullTopic }}</code>
                  <button @click="copyToClipboard(fullTopic, 'topic')" class="mini-copy-btn">
                    <i class="fas fa-copy"></i>
                  </button>
                </div>
              </div>
              <p>• Check your internet connection</p>
              <p>• Verify app notification permissions are enabled</p>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Toast Notification -->
    <div v-if="showToast" class="toast" :class="toastType">
      <i :class="toastIcon"></i> {{ toastMessage }}
    </div>
  </div>
</template>

<script>

export default {
  name: 'NtfySetup',
  
  props: {
    driverId: {
      type: String,
      required: true
    }
  },
  
  data() {
    return {
      serverUrl: 'https://guiders.up.railway.app',
      copyButtonText: 'Copy',
      isSendingTest: false,
      testResult: null,
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      toastIcon: 'fas fa-check-circle'
    };
  },
  
  computed: {
    fullTopic() {
      return `${this.driverId}`;
    },
    userTopicUrl() {
      return `${this.serverUrl}/${this.fullTopic}`;
    },
    subscriptionData() {
      return JSON.stringify({
        topic: this.fullTopic,
        server: this.serverUrl
      });
    }
  },
  
  methods: {
    copyTopicUrl() {
      this.copyToClipboard(this.userTopicUrl, 'URL');
    },
    
    async copyToClipboard(text, type = '') {
      try {
        await navigator.clipboard.writeText(text);
        this.showToastMessage(`${type ? type + ' ' : ''}copied to clipboard!`, 'success', 'fas fa-check-circle');
      } catch (err) {
        console.error('Failed to copy: ', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.showToastMessage(`${type ? type + ' ' : ''}copied to clipboard!`, 'success', 'fas fa-check-circle');
      }
    },
    
    copyBothToClipboard() {
      const text = `Topic: ${this.fullTopic}\nServer: ${this.serverUrl}`;
      this.copyToClipboard(text, 'Topic & Server');
    },
    
    showToastMessage(message, type = 'success', icon = 'fas fa-check-circle') {
      this.toastMessage = message;
      this.toastType = type;
      this.toastIcon = icon;
      this.showToast = true;
      
      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },
    
    async sendTestNotification() {
      this.isSendingTest = true;
      this.testResult = null;
      
      try {
        const response = await fetch(this.userTopicUrl, {
          method: 'POST',
          headers: {
            'Title': 'Guiders Test Notification',
            'Tags': 'white_check_mark,test',
            'Priority': '3'
          },
          body: 'Test notification from Guiders! Your setup is working correctly.'
        });
        
        if (response.ok || response.status === 200) {
          this.testResult = {
            type: 'success',
            message: 'Test notification sent! Check your device(s).',
            icon: 'fas fa-check-circle'
          };
        } else {
          throw new Error(`Server returned ${response.status}`);
        }
      } catch (error) {
        console.error('Test notification error:', error);
        this.testResult = {
          type: 'error',
          message: `Failed to send test: ${error.message}. Please check your setup.`,
          icon: 'fas fa-exclamation-circle'
        };
      } finally {
        this.isSendingTest = false;
        setTimeout(() => {
          this.testResult = null;
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
.ntfy-setup-container {
  background-color: white;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

.setup-header {
  text-align: center;
  margin-bottom: 40px;
}

.setup-header h1 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.subtitle {
  color: #7f8c8d;
  font-size: 1.1em;
}

.step-card {
  background: white;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0a6;
}

.step-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #3498db;
  color: white;
  border-radius: 50%;
  font-weight: bold;
  margin-right: 15px;
  font-size: 18px;
}

.step-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.4em;
}

.topic-url-container {
  display: flex;
  gap: 10px;
  margin: 15px 0;
}

.topic-input {
  flex: 1;
  padding: 12px 15px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-family: monospace;
  font-size: 14px;
  background: #f8f9fa;
  cursor: pointer;
}

.topic-input:focus {
  outline: none;
  border-color: #3498db;
}

.copy-btn, .test-btn, .quick-copy-btn {
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.copy-btn {
  background: #3498db;
  color: white;
}

.copy-btn:hover {
  background: #2980b9;
  transform: translateY(-1px);
}


.note {
  color: #7f8c8d;
  font-size: 0.9em;
  margin-top: 10px;
}

.os-icon {
  font-size: 2em;
  margin-bottom: 20px;
  color: #2c3e50;
}

.instructions-list {
  padding-left: 20px;
  margin: 20px 0;
}

.instructions-list li {
  margin-bottom: 15px;
  line-height: 1.5;
}

.copy-field-group {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 5px 0;
  background: #f8f9fa;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.inline-code {
  flex: 1;
  background: #f1f1f1;
  padding: 6px 10px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9em;
  color: #d63384;
  border: 1px solid #ddd;
}

.mini-copy-btn {
  background: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.8em;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
}

.mini-copy-btn:hover {
  background: #2980b9;
}

.curl-copy {
  align-self: flex-start;
  margin-top: 10px;
}

.quick-copy-section {
  margin: 20px 0;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.quick-copy-section h4 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

.quick-copy-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.quick-copy-btn {
  background: #6c757d;
  color: white;
  font-size: 0.9em;
  padding: 8px 12px;
}

.quick-copy-btn:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

.quick-copy-btn.both {
  background: #3498db;
}

.quick-copy-btn.both:hover {
  background: #2980b9;
}

.app-store-badges {
  margin-top: 20px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.app-store-badge, .play-store-badge {
  height: 60px;
  border-radius: 8px;
}

.test-section {
  margin: 20px 0;
}

.test-btn {
  background: #2ecc71;
  color: white;
}

.test-btn:hover:not(:disabled) {
  background: #27ae60;
  transform: translateY(-1px);
}

.test-btn:disabled {
  background: #95a5a6;
  cursor: not-allowed;
  transform: none;
}

.test-result {
  padding: 12px;
  border-radius: 6px;
  margin-top: 10px;
  font-size: 0.95em;
  display: flex;
  align-items: center;
  gap: 10px;
}

.test-result.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.test-result.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.troubleshooting-tips {
  margin-top: 20px;
}

.tip {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  border-left: 4px solid #3498db;
}

.curl-command {
  flex: 1;
  display: block;
  background: #2c3e50;
  color: #ecf0f1;
  padding: 10px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 0.9em;
  margin: 10px 0;
  overflow-x: auto;
  white-space: nowrap;
  border: 1px solid #34495e;
}

.security-info ul {
  padding-left: 20px;
  margin-top: 10px;
}

.security-info li {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.security-info a {
  color: #3498db;
  text-decoration: none;
}

.security-info a:hover {
  text-decoration: underline;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 25px;
  max-width: 450px;
  width: 90%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #7f8c8d;
  line-height: 1;
}

.close-btn:hover {
  color: #2c3e50;
}

.toast {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #28a745;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  animation: slideIn 0.3s ease;
  z-index: 1001;
}

.toast.success {
  background: #28a745;
}

.toast.error {
  background: #dc3545;
}

.toast.info {
  background: #17a2b8;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .topic-url-container {
    flex-direction: column;
  }
  
  .copy-btn, .quick-copy-btn {
    width: 100%;
    margin: 5px 0;
    justify-content: center;
  }
  
  .quick-copy-buttons {
    flex-direction: column;
  }
  
  .app-store-badges {
    justify-content: center;
  }
  
  .step-card {
    padding: 20px 15px;
  }
  
  .copy-field-group {
    flex-direction: column;
    align-items: stretch;
  }
  
  .mini-copy-btn {
    align-self: flex-start;
  }
  
  .curl-command {
    font-size: 0.8em;
    white-space: normal;
    word-break: break-all;
  }
  
  .toast {
    left: 20px;
    right: 20px;
    bottom: 10px;
  }
}
</style>